import React, { FC, useState } from "react";
import { Box, IconButton, Menu, MenuItem, ListItemIcon, ListItemText } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MenuIcon from "@mui/icons-material/Menu";
import { GkvEditHeaderActionsProps } from "../types/interface";
import { BillingStepper } from "../../../../Base/components/Stepper";
import ToggleUISwitch from "../../common/components/ToggleUISwitch";
import ValidationProgressBar from "../../GKVNew/components/ValidationProgressBar";
import { colors } from "../../GKVNew/styles/tokens";

/**
 * Sticky header for the edit workspace.
 *
 * Layout: BillingStepper (flex:1) + Hamburger menu (right, subtle dark style)
 * Burger menu: "Rezept hinzufügen" + Test toggle (ToggleUISwitch)
 */
const GkvEditHeaderActions: FC<GkvEditHeaderActionsProps> = ({
  activeStep,
  isValidating,
  validationProgress,
  testToggleChecked,
  onAddRx,
  onTestToggleChange,
}) => {
  // Burger menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuAction = (action: () => void) => {
    action();
    handleMenuClose();
  };

  const handleToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onTestToggleChange(event);
    handleMenuClose();
  };

  // Edit flow: offset by 1 since step 0 ("Rezepte Zusammensuchen") is already done
  const stepperActiveStep = activeStep + 1;

  return (
    <Box>
      {/* Row 1: Stepper + Hamburger — same as GKVNew */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {/* Stepper */}
        <Box sx={{ flex: 1 }}>
          <BillingStepper activeStep={stepperActiveStep} hideProgress />
        </Box>

        {/* Hamburger menu — GKVNew style */}
        <IconButton
          onClick={handleMenuClick}
          size="small"
          sx={{
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            color: colors.textOnDark,
            padding: 0.75,
            border: "1px solid rgba(255,255,255,0.2)",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.2)",
            },
          }}
        >
          <MenuIcon fontSize="small" />
        </IconButton>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
          <MenuItem onClick={() => handleMenuAction(onAddRx)}>
            <ListItemIcon>
              <AddIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Rezept hinzufügen</ListItemText>
          </MenuItem>
          <MenuItem sx={{ justifyContent: "center" }}>
            <ToggleUISwitch
              checked={testToggleChecked}
              onChange={handleToggleChange}
              size="small"
            />
          </MenuItem>
        </Menu>
      </Box>

      {/* Validation progress bar */}
      {isValidating && <ValidationProgressBar progress={validationProgress} />}
    </Box>
  );
};

export default React.memo(GkvEditHeaderActions);
